// The Workflow board's columns, and which status names land in each.
//
// Extracted from WorkflowPage so the mapping can be tested directly: the board
// matches a card to a column purely by status NAME, so a status missing from
// every column silently disappears from the board rather than erroring. That
// failure is invisible until someone notices a card is gone, which is exactly
// the kind of thing worth a test.
//
// 2026-08-28: the "Received Docs" column was removed at the firm's request --
// receiving documents is the start of the work, not a holding state of its
// own. Its status name moved into In Progress rather than being dropped, so
// existing cards flow there instead of vanishing.
//
// 2026-10-30: the "Completed" column was removed at the firm's request. A card
// in the last column has a Complete button instead, and a finished card leaves
// the board (the page asks the API for hide_completed). "Ready for efile" moved
// into that last column -- it is the step before completion, not completion.

// The status a rolled-forward file moves to on the 1st of its year-end month
// (backend/clients/waiting_docs.py). The firm's wording is not final: rename
// it here, in the StatusOption (Settings), and in settings.WAITING_FOR_DOCS_STATUS.
export const WAITING_FOR_DOCS_STATUS = 'Waiting for initial documents';

// Statuses that mean "finished". A card carrying one has no column; the board
// filters it out rather than letting it fall back to the first column.
export const COMPLETED_STATUSES = ['Completed'];

export const WORKFLOW_STRUCTURE = [
  {
    id: 'waiting_for_docs',
    name: 'Waiting For Docs',
    icon: '📥',
    color: '#9ca3af',
    // 'No status' is a real StatusOption carried by untriaged tasks. It
    // belonged to no column, so 13 live cards were invisible on the board.
    // Untriaged is the start of the pipeline, so it lands here.
    statuses: ['Not Started', WAITING_FOR_DOCS_STATUS, 'No status'],
  },
  {
    id: 'in_progress',
    name: 'In Progress',
    icon: '⚙️',
    color: '#f59e0b',
    statuses: ['Received docs', 'In progress'],
  },
  {
    id: 'clear_queries',
    name: 'Clear Queries',
    icon: '❓',
    color: '#ef4444',
    // 'Waiting for client' is the same shape of problem as 'No status':
    // a live status in no column. You are blocked on them, which is what
    // Clear Queries means.
    statuses: ['Clear queries', 'Waiting for client'],
  },
  {
    id: 'in_review',
    name: 'In Review',
    icon: '👁️',
    color: '#3b82f6',
    statuses: ['In review'],
  },
  {
    id: 'send_client_drafts',
    name: 'Send Client Drafts',
    icon: '📨',
    color: '#8b5cf6',
    statuses: ['Send Client Docs'],
  },
  {
    id: 'drafts_sent',
    name: 'Drafts Sent',
    icon: '📤',
    color: '#f97316',
    statuses: ['Waiting for signatures', 'Ready for efile'],
  },
];

// The column whose cards get the Complete button.
export const LAST_COLUMN_ID = WORKFLOW_STRUCTURE[WORKFLOW_STRUCTURE.length - 1].id;

export function isCompletedStatus(statusName) {
  if (!statusName) return false;
  const needle = String(statusName).toLowerCase().trim();
  return COMPLETED_STATUSES.some((s) => s.toLowerCase() === needle);
}

// Where a card goes when its status matches no column at all.
//
// Status names are free text on StatusOption, so matching is case-insensitive
// and whitespace-trimmed -- "Received docs", "Received Docs" and " received
// docs " must all land in the same place.
//
// This is the safety net, and it is the point of the whole exercise: the board
// places cards by status NAME, so any status somebody adds in Settings that
// nobody thinks to list here would make those cards silently disappear. That
// is how 14 live cards went missing before anyone noticed. Falling back to the
// first column means new or retired statuses surface as work to triage rather
// than as work that evaporates.
export const FALLBACK_COLUMN_ID = 'waiting_for_docs';

export function getColumnForStatus(statusName, columns = WORKFLOW_STRUCTURE) {
  if (!statusName) return null;
  // A finished card belongs to no column -- never to the fallback.
  if (isCompletedStatus(statusName)) return null;
  const needle = String(statusName).toLowerCase().trim();
  const match = columns.find((col) =>
    col.statuses.some((s) => s.toLowerCase() === needle)
  );
  if (match) return match;
  // An unrecognised but non-empty status is still real work. Never drop it.
  return columns.find((col) => col.id === FALLBACK_COLUMN_ID) || columns[0] || null;
}
